<template>
    <Select
        class="w-100"
        :value="valueComputed"
        :placeholder="placeholder"
        :allowClear="allowClear"
        :showSearch="true"
        :filterOption="() => true"
        :mode="mode"
        :disabled="disabled"
        :showArrow="!disabled"
        @search="onSearch"
        @update:value="onUpdateValue"
        @select="onSelect"
        @deselect="onDeselect"
        @dropdownVisibleChange="onDropdownVisibleChange"
    >
        <SelectOption v-for="item in itemsComputed" :key="getKeyValue({ record: item, props })" :value="getKeyValue({ record: item, props })">
            <component v-if="renderLabel" :is="renderLabel({ item })"></component>
            <span v-else>{{ optLabel ? lodash.get(item, optLabel) : item }}</span>
        </SelectOption>
        <template v-slot:dropdownRender="{ menuNode: menu }">
            <div class="p-2" v-if="mode === 'multiple' && canCheckAll" @mousedown="(e) => e.preventDefault()">
                <a href="javascript:void(0)" @click="onSelectAll">{{ $t("button.selectAll") }}</a>
                <span class="mx-2">|</span>
                <a href="javascript:void(0)" @click="onDeselectAll">{{ $t("button.deselectAll") }}</a>
            </div>
            <template v-if="data.isLoading">
                <div class="p-2">
                    <Spin :spinning="true" class="mr-2" />
                    <span class="text-break">{{ $t("components.select.search.searching.title", { key: data.searchQuery }) }}</span>
                </div>
            </template>
            <template v-else>
                <VNodes :vnodes="menu"></VNodes>
            </template>
        </template>
    </Select>
</template>

<script>
import { computed, defineComponent, reactive } from "vue";
import { Select, Spin } from "ant-design-vue";
import {
    createOnSearch,
    createReloadItems,
    createOnUpdateValue,
    createOnSelect,
    createOnDeselect,
    createOnSelectAll,
    createOnDeselectAll,
    createOnDropdownVisibleChange,
    getValues,
    getItems,
    getKeyValue,
} from "./c-select.logic";
import lodash from "lodash";

export default defineComponent({
    components: {
        VNodes: (_, { attrs }) => attrs.vnodes,
        Select,
        SelectOption: Select.Option,
        Spin,
    },
    props: {
        value: {},
        placeholder: {},
        disabled: {},
        list: {},
        optKey: { default: "id" },
        optLabel: { default: "name" },
        renderLabel: {},
        allowClear: { default: (props) => (props.mode ? false : true) },
        mode: { default: undefined },
        selectWithIsSave: { default: true },
        rowKey: {},
        canCheckAll: { default: false },
    },
    emits: ["update:value", "select", "deselect"],
    setup(props, { emit }) {
        const data = reactive({ items: [], searchQuery: null, isLoading: false, open: false, isOpenFirst: true });
        const reloadItems = createReloadItems({ data, props });
        const debounceReloadItems = lodash.debounce(reloadItems, 250);
        const onSearch = createOnSearch({ data, debounceReloadItems });
        const onUpdateValue = createOnUpdateValue({ data, props, emit });
        const onSelect = createOnSelect({ data, props, emit });
        const onDeselect = createOnDeselect({ data, props, emit });
        const onSelectAll = createOnSelectAll({ data, props, emit, onSelect });
        const onDeselectAll = createOnDeselectAll({ data, props, emit, onDeselect });
        const onDropdownVisibleChange = createOnDropdownVisibleChange({ data, onSearch, reloadItems });
        const valueComputed = computed(() => getValues({ props }));
        const itemsComputed = computed(() => getItems({ data, props }));
        return {
            props,
            data,
            emit,
            onSearch,
            onUpdateValue,
            onSelect,
            onDeselect,
            onSelectAll,
            onDeselectAll,
            onDropdownVisibleChange,
            valueComputed,
            itemsComputed,
            getKeyValue,
            lodash,
        };
    },
});
</script>
